<script setup lang="ts">

import {onMounted, ref} from 'vue';
import AdminReportItemKpi from '@/views/admin/AdminReportItemKpi.vue';
import {ElDialog} from 'element-plus';
import KpiIcon from '@/views/icons/KpiIcon.vue';
import AdminReportItemMijlpaal from '@/views/admin/AdminReportItemMijlpaal.vue';
import {
    CabReportItem,
    EntryType,
    ICON_DOT,
    ICON_ERROR,
    ICON_RESULT,
    ICON_SUB_RESULT,
    renderCabPeriod,
    renderReportItemShort,
    TraceEntry,
} from 'common';
import {listObjects} from '@/base/callBackend.js';

const props          = defineProps({
    title: {
        type    : String,
        required: true,
    },
    expression: {
        type    : String,
        required: false,
    },
    trace: {
        type    : String,
        required: true,
    },
    hasErrors: {
        type    : Boolean,
        required: true,
    },
});
const editReportItem = ref<CabReportItem | undefined>();
const dialogVisible  = ref<boolean>();
const traceEntries   = ref<TraceEntry[]>([]);
const uuid2riMap     = ref<Map<string, CabReportItem> | null>(null);

onMounted(async () => {
    const traceLinesRaw: TraceEntry[]     = TraceEntry.jsonArray(props.trace);
    const traceEntriesLocal: TraceEntry[] = traceLinesRaw.filter((item, index) => traceLinesRaw.findIndex(x => x.equals(item)) === index);

    const uuids: string[]      = traceEntriesLocal.flatMap(e => e.getUuids());
    const ris: CabReportItem[] = await listObjects(CabReportItem, {uuid: uuids});
    uuid2riMap.value           = new Map(ris.map(item => [item.uuid, item]));
    traceEntries.value         = traceEntriesLocal;
});

function showInDialog(uuid: string) {
    editReportItem.value = uuid2riMap.value!.get(uuid) as CabReportItem;
    dialogVisible.value  = true;
}

function renderShort(uuid: string) {
    const item: CabReportItem | undefined = uuid2riMap.value!.get(uuid);
    if (!item) {
        return `ERROR:${uuid}`;
    } else {
        return renderReportItemShort(item);
    }
}

function renderTitle(uuid: string) {
    const item: CabReportItem | undefined = uuid2riMap.value!.get(uuid);
    if (!item) {
        return `ERROR:${uuid}`;
    } else {
        return `periode: ${renderCabPeriod(item.period[0])}\norganisatie: ${item.organization[0].name}\nthema: ${item.theme[0].name}\nnaam: ${item.kpi[0].name}`.replaceAll(' ', ' ');
    }
}
</script>

<template>
    <div class="overflow-y-auto overflow-x-auto max-h-96 m-2 border-2 border-black"
         :class="hasErrors?'bg-red-200':'bg-green-200'"
    >
        <div class="font-bold pl-2">
            {{ title }}:
        </div>
        <div class="whitespace-nowrap text-xs p-3">
            <template v-if="traceEntries.length!=0">
                <div class="p-0.5 pl-3 text-white" :class="hasErrors?'bg-red-600':'bg-green-600'"
                >
                    <span class="mr-2 inline-block min-w-4"></span>
                    <span class="mr-2 inline-block min-w-96 font-semibold">waarde</span>
                    <span class="mr-2 inline-block min-w-4"></span>
                    <span class="mr-2 inline-block min-w-96 font-semibold">veld/expressie/variabele</span>
                    <span class="mr-2 inline-block min-w-4"></span>
                    <span class="mr-2 inline-block min-w-96 font-semibold">context</span>
                </div>
            </template>
            <template v-for="(e, index) of traceEntries"
                      :key="index"
            >
                <template v-if="e.entryType==EntryType.START">
                    <!-- no-show -->
                </template>
                <template v-else-if="e.entryType==EntryType.FIELD_ACCESS">
                    <div class="p-0.5 pl-3">
                        <span class="mr-2 inline-block min-w-4">{{ e.renderIcon() }}</span>
                        <span v-if="e.valueIsItem"
                              class='mr-2 inline-block min-w-96 underline text-blue-800 cursor-pointer'
                              @click="showInDialog(e.value!)"
                              :title="renderTitle(e.value)">{{ renderShort(e.value) }}</span>
                        <span v-else
                              class="mr-2 inline-block min-w-96 font-extrabold">{{ e.renderValue() }}</span>
                        <span class="mr-2 min-w-4">{{ ICON_SUB_RESULT }}</span>
                        <span class="mr-2 inline-block min-w-96">{{ e.fieldname }}</span>
                        <span class="mr-2 min-w-4">{{ ICON_DOT }}</span>
                        <span v-if="e.uuid"
                              class="mr-2 inline-block min-w-96 underline text-blue-800 cursor-pointer"
                              @click="showInDialog(e.uuid!)"
                              :title="renderTitle(e.uuid)">{{ renderShort(e.uuid) }}</span>
                    </div>
                </template>
                <template v-else-if="e.entryType==EntryType.ASSIGN">
                    <div class="p-0.5 pl-3">
                        <span class="mr-2 min-w-4">{{ e.renderIcon() }}</span>
                        <span v-if="e.valueIsItem"
                              class='mr-2 inline-block min-w-96 underline text-blue-800 cursor-pointer'
                              @click="showInDialog(e.value!)"
                              :title="renderTitle(e.value)">{{ renderShort(e.value) }}</span>
                        <span v-else
                              class="mr-2 inline-block min-w-96 font-extrabold">{{ e.renderValue() }}</span>
                        <span class="mr-2 min-w-4">{{ ICON_SUB_RESULT }}</span>
                        <span class="mr-2 inline-block min-w-96">{{ e.fieldname }}</span>
                    </div>
                </template>
                <template v-else-if="e.entryType==EntryType.END">
                    <div class="p-0.5 pl-3">
                        <span class="mr-2 min-w-4">{{ ICON_RESULT }}</span>
                        <span v-if="e.valueIsItem"
                              class='mr-2 inline-block min-w-96 underline text-blue-800 cursor-pointer'
                              @click="showInDialog(e.value!)"
                              :title="renderTitle(e.value)">{{ renderShort(e.value) }}</span>
                        <span v-else
                              class="mr-2 inline-block min-w-96 font-extrabold">{{ e.renderValue() }}</span>
                    </div>
                </template>
                <template v-else-if="e.entryType==EntryType.ERROR && e.exc">
                    <!-- return `${ICON_ERROR} ${this.message} exc: ${this.exc instanceof Error ? String(this.exc) : this.exc.message}`;-->
                    <div class="p-0.5 pl-3">
                        <span class="mr-2 min-w-4">{{ ICON_ERROR }}</span>
                        <span class="mr-2">{{ e.message }}</span>
                        <span v-if="e.uuid"
                              class="mr-2 inline-block min-w-96 underline text-blue-800 cursor-pointer"
                              @click="showInDialog(e.uuid!)"
                              :title="renderTitle(e.uuid)">{{ renderShort(e.uuid) }}</span>
                    </div>
                </template>
                <template v-else-if="e.entryType==EntryType.ERROR ">
                    <div class="p-0.5 pl-3">
                        <span class="mr-2 min-w-4">{{ ICON_ERROR }}</span>
                        <span class="mr-2">{{ e.message }}</span>
                        <span v-if="e.uuid"
                              class="mr-2 inline-block min-w-96 underline text-blue-800 cursor-pointer"
                              @click="showInDialog(e.uuid!)"
                              :title="renderTitle(e.uuid)">{{ renderShort(e.uuid) }}</span>
                    </div>
                </template>
            </template>
        </div>
        <pre v-if="expression"
             class="m-2 p-2 w-maxfont-thin text-xs"
             :class="hasErrors?'bg-red-100':'bg-green-100'"
        >{{ expression }}</pre>
    </div>

    <el-dialog
        v-model="dialogVisible"
        width="90%"
        align-center
        destroy-on-close
        :show-close="false"
        v-if="editReportItem"
        :draggable="true"
    >
        <template #header>
            <div class="flex flex-row items-baseline text-lg font-semibold my-0.5 mx-3 cursor-pointer">
                <KpiIcon :kpi="editReportItem.kpi[0]"/>
                {{ editReportItem.kpi[0].name }}
            </div>
        </template>

        <AdminReportItemMijlpaal
            v-if="editReportItem.isMijlpaal"
            :item="editReportItem"
            :show-org-theme="true"
        />
        <AdminReportItemKpi
            v-if="!editReportItem.isMijlpaal"
            :item="editReportItem"
            :show-org-theme="true"
        />

    </el-dialog>
</template>
